import { useState } from "react";
import { useAuth } from "../../context/LoginContext/AuthContext";
import { loadRazorpay } from "../ParentAdmin/Register/Payment/LoadRazorpay";
import useCreatePaymentOrder from "../../../hooks/payment/useCreatePaymentOrder";
import useConfirmRazorPayPayment from "../../../hooks/payment/useConfirmRazorPayPayment";
import SuccessModal from "../../common/SuccessModal";

/**
 * RazorPayButton Component
 *
 * Handles Razorpay payment initiation and verification.
 * This is a reusable component that can be used in Payment.jsx
 *
 * @param {Object} props
 * @param {string|number} props.invoiceId - Invoice ID to pay for
 * @param {Object} props.invoice - Invoice object with details
 * @param {number} props.amount - Amount to pay (pending amount)
 * @param {Function} props.onSuccess - Callback on successful payment
 * @param {Function} props.onCancel - Callback on payment cancellation
 * @param {boolean} props.disabled - Whether the button is disabled
 * @param {string} props.currency - Currency code (e.g., "INR","USD")
 */
export const RazorPayButton = ({
  invoiceId,
  invoice,
  amount,
  onSuccess,
  onCancel,
  disabled = false,
  currency,
}) => {
  const { user } = useAuth();
  const { createPaymentOrder, error: createPaymentOrderError } =
    useCreatePaymentOrder();
  const { confirmPayment } = useConfirmRazorPayPayment();

  const [isProcessing, setIsProcessing] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    type: "success",
    title: "Success",
    message: "",
  });

  /**
   * Show success/error modal
   */
  const showResultModal = (type, title, message) => {
    setModalConfig({ type, title, message });
    setShowModal(true);
  };

  /**
   * Handle modal close
   */
  const handleModalClose = () => {
    setShowModal(false);
    // If success, trigger the onSuccess callback
    if (modalConfig.type === "success" && onSuccess) {
      onSuccess();
    }
  };

  /**
   * Initiates Razorpay payment flow
   */
  const handleRazorpayPayment = async () => {
    const amountFloat = parseFloat(amount);

    if (isNaN(amountFloat) || amountFloat <= 0) {
      showResultModal("error", "Error", "Invalid payment amount");
      return;
    }

    setIsProcessing(true);

    try {
      // Step 1: Load Razorpay script
      const isLoaded = await loadRazorpay();
      if (!isLoaded) {
        showResultModal(
          "error",
          "Error",
          "Failed to load Razorpay. Please try again."
        );
        setIsProcessing(false);
        return;
      }

      // Step 2: Create Razorpay order
      const orderResult = await createPaymentOrder({
        invoice_id: invoiceId,
        gateway: "razorpay",
        amount: amountFloat,
        currency: currency,
      });

      if (!orderResult || !orderResult.order_id) {
        showResultModal(
          "error",
          "Error",
          createPaymentOrderError || "Failed to create payment order"
        );
        setIsProcessing(false);
        return;
      }

      // Step 3: Initialize Razorpay checkout
      const options = {
        key: process.env.REACT_APP_PUBLIC_RAZORPAY_KEY_ID,
        order_id: orderResult.order_id,
        amount: orderResult.amount,
        currency: orderResult.currency,
        name: "School Subscription",
        description: `Payment for Invoice ${invoice?.invoice_no || invoiceId}`,
        theme: { color: "#2d8cf0" },

        // Payment success handler
        handler: async function (response) {
          try {
            await confirmPayment({
              invoice_id: invoiceId,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              amount: orderResult.amount,
              currency: orderResult.currency,
            });

            showResultModal(
              "success",
              "Payment Successful",
              `Your payment of ${orderResult.currency === "INR" ? "₹" : "$"} ${amountFloat.toFixed(2)} has been processed successfully!`
            );
          } catch (err) {
            showResultModal(
              "error",
              "Payment Verification Failed",
              err.message || "Payment verification failed. Please contact support."
            );
          } finally {
            setIsProcessing(false);
          }
        },

        // Modal dismiss handler
        modal: {
          ondismiss: function () {
            showResultModal(
              "error",
              "Payment Cancelled",
              "You have cancelled the payment. Please try again when ready."
            );
            setIsProcessing(false);
            if (onCancel) {
              onCancel();
            }
          },
        },

        // Prefill user details
        prefill: {
          name: user?.name || "Guest User",
          email: user?.email || "",
          contact: user?.phone || "",
        },
      };

      const rzp = new window.Razorpay(options);

      // Payment failure handler
      rzp.on("payment.failed", function (response) {
        showResultModal(
          "error",
          "Payment Failed",
          response.error.description || "Payment failed. Please try again."
        );
        setIsProcessing(false);
      });

      // Open Razorpay checkout
      rzp.open();
    } catch (error) {
      console.error("Razorpay payment error:", error);
      showResultModal(
        "error",
        "Payment Error",
        error.message || "Failed to initiate payment. Please try again."
      );
      setIsProcessing(false);
    }
  };

  return (
    <>
      <button
        className="btn btn-primary btn-lg w-100"
        onClick={handleRazorpayPayment}
        disabled={disabled || isProcessing || parseFloat(amount) <= 0}
      >
        {isProcessing ? (
          <>
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
            ></span>
            Processing...
          </>
        ) : (
          <>
            <i className="fa fa-credit-card me-2"></i>
            Pay {currency === "INR" ? "₹" : "$"}{parseFloat(amount).toFixed(2)} with Razorpay
          </>
        )}
      </button>

      {/* Success/Error Modal */}
      <SuccessModal
        showModal={showModal}
        handleClose={handleModalClose}
        type={modalConfig.type}
        title={modalConfig.title}
        message={modalConfig.message}
        primaryButtonText="OK"
      />
    </>
  );
};

export default RazorPayButton;
